import { ArrowDropDown, Check } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputBase,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import Loader from '../../../../../common/Loader';
import useCarrierRequest from '../../../../../../requests/CarrierRequest';

const DeviceSelect = ({
  carrierDevice,
  countryId,
  carrierId,
  disabled = false,
  required = false,
  fullWidth = false,
  onChange,
  errorHandler,
}) => {
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [showDeviceDialog, setShowDeviceDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState({});
  const [filter, setFilter] = useState('');

  const { getSelectableDevices } = useCarrierRequest();

  useEffect(() => {
    setSelectedDevice(carrierDevice || {});
  }, [carrierDevice]);

  useEffect(() => {
    fetchDevices();
  }, [countryId, carrierId]);

  const filteredDevices = useMemo(() => {
    return devices.filter((device) =>
      device.device_name.toLowerCase().includes(filter.toLowerCase())
    );
  }, [devices, filter]);

  const fetchDevices = async () => {
    if (!countryId || !carrierId) return;

    setLoadingDevices(true);
    setDevices(await getSelectableDevices(countryId, carrierId, errorHandler));
    setLoadingDevices(false);
  };

  let filterTimeout;
  const filterSelectable = (e) => {
    clearTimeout(filterTimeout);
    filterTimeout = setTimeout(() => {
      setFilter(e.target.value);
    }, 500);
  };

  return (
    <Stack sx={{ flex: fullWidth ? 1 : 'auto' }}>
      <InputBase
        readOnly
        value={selectedDevice?.device_name || ''}
        onClick={() => !disabled && setShowDeviceDialog(true)}
        placeholder="Select Device ..."
        sx={{
          border: '1px solid',
          borderColor: 'grey.400',
          borderRadius: 1.5,
          padding: '8px 8px 8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
          '&:hover': {
            borderColor: 'text.primary',
          },
          '&:has(input:focus)': {
            borderWidth: 2,
            borderColor: 'secondary.light',
          },
          '& .MuiInputBase-input': {
            flex: 1,
            cursor: 'pointer',
            padding: 0,
          },
        }}
        endAdornment={<ArrowDropDown sx={{ color: 'grey.600' }} />}
      />

      <Dialog
        open={showDeviceDialog}
        maxWidth="lg"
        fullWidth
        onClose={() => setShowDeviceDialog(false)}
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h2">Select Device</Typography>
            <TextField
              label="Filter Devices"
              onInput={filterSelectable}
              size="small"
            ></TextField>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Table stickyHeader sx={{ overflow: 'visible' }}>
              <TableHead>
                <TableRow>
                  <TableCell>Device</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Reply TN</TableCell>
                  <TableCell>Accepts Alphas</TableCell>
                  <TableCell>Route</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingDevices && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Loader />
                    </TableCell>
                  </TableRow>
                )}
                {!loadingDevices &&
                  filteredDevices.map((device) => (
                    <TableRow
                      key={device.device_id}
                      id={`device-${device.device_id}`}
                      hover
                      onClick={() => {
                        setShowDeviceDialog(false);
                        setSelectedDevice(device);
                        onChange(device);
                      }}
                    >
                      <TableCell name="name">
                        <Stack direction="row" gap={2} alignItems="center">
                          {device.device_name}
                          {selectedDevice?.device_id === device.device_id && (
                            <Tooltip title="selected">
                              <Check color="success" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{device.device_type_meaning}</TableCell>
                      <TableCell>{device.device_status_meaning}</TableCell>
                      <TableCell>{device.device_class_meaning}</TableCell>
                      <TableCell>{device.reply_tn}</TableCell>
                      <TableCell>
                        {device.accepts_alphas === 'Y' && (
                          <Check color="success" />
                        )}
                      </TableCell>
                      <TableCell>{device.route_name || '-'}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            color="secondary"
            onClick={() => setShowDeviceDialog(false)}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DeviceSelect;
